import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, X, Loader2, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

/**
 * In-app PDF preview modal.
 *
 * Fetches both:
 *   - PDF pages rendered as PNG images (server-side via pdf2image / poppler).
 *     <img> always renders in any browser/iframe context, unlike PDF iframes
 *     which Chrome refuses to display from blob URLs.
 *   - The full PDF as base64, used by the download button (anchor download).
 */
export default function PdfPreviewModal({ open, onClose, previewUrl, downloadUrl, filename }) {
  const [pages, setPages] = useState([]);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serverFilename, setServerFilename] = useState(filename);
  const [copied, setCopied] = useState(false);

  // Detect sandboxed iframe (Emergent preview) — downloads can be blocked here.
  const inSandbox = useMemo(() => {
    try { return window.self !== window.top; } catch { return true; }
  }, []);

  useEffect(() => {
    if (!open || !previewUrl) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPages([]);
    setPdfDataUrl(null);

    fetch(previewUrl, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setPages(data.pages || []);
        if (data.pdf_base64) {
          setPdfDataUrl(`data:application/pdf;base64,${data.pdf_base64}`);
        }
        if (data.filename) setServerFilename(data.filename);
      })
      .catch((e) => {
        console.error(e);
        setError(e.message || "Erreur de chargement");
        toast.error("Impossible de charger le PDF");
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [open, previewUrl]);

  const handleDownload = () => {
    if (!downloadUrl) return;
    // Best-effort: works in unsandboxed contexts (deployed app, direct URL).
    window.location.href = downloadUrl;
    toast.success("Téléchargement lancé");
  };

  const handleCopyLink = async () => {
    if (!downloadUrl) return;
    try {
      await navigator.clipboard.writeText(downloadUrl);
      setCopied(true);
      toast.success("Lien copié — collez-le dans un nouvel onglet");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // Fallback: legacy execCommand
      const textarea = document.createElement("textarea");
      textarea.value = downloadUrl;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.success("Lien copié");
        setTimeout(() => setCopied(false), 2500);
      } catch {
        toast.error("Impossible de copier le lien");
      }
      textarea.remove();
    }
  };

  const handleOpenNewTab = () => {
    if (!downloadUrl) return;
    window.open(downloadUrl, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-4xl w-[96vw] h-[92vh] sm:h-[92vh] p-0 gap-0 flex flex-col"
        data-testid="pdf-preview-modal"
      >
        <DialogHeader className="px-3 sm:px-5 py-2.5 sm:py-3 border-b border-slate-100 flex flex-row items-center justify-between gap-2 sm:gap-3 space-y-0">
          <DialogTitle className="font-display text-sm sm:text-base font-semibold text-slate-900 truncate min-w-0 flex-1">
            {serverFilename || filename || "Rapport PDF"}
          </DialogTitle>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopyLink}
              disabled={!downloadUrl || loading}
              className="rounded-lg h-9 border-slate-200 px-2 sm:px-3"
              data-testid="pdf-copy-link-btn"
              title="Copier le lien du PDF — collez-le dans un nouvel onglet pour le télécharger"
            >
              {copied ? <Check className="h-4 w-4 sm:mr-1.5 text-emerald-600" /> : <Copy className="h-4 w-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">{copied ? "Copié" : "Copier le lien"}</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenNewTab}
              disabled={!downloadUrl || loading}
              className="rounded-lg h-9 border-slate-200 px-2 sm:px-3"
              data-testid="pdf-open-tab-btn"
            >
              <ExternalLink className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Ouvrir</span>
            </Button>
            <Button
              size="sm"
              onClick={handleDownload}
              disabled={!downloadUrl || loading}
              className="bg-[#3b82f6] hover:bg-blue-600 text-white rounded-lg h-9 px-2 sm:px-3"
              data-testid="pdf-download-btn"
            >
              <Download className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Télécharger</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="rounded-lg h-9 w-9 p-0"
              data-testid="pdf-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        {inSandbox && (
          <div
            className="px-5 py-2.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-center justify-between gap-3"
            data-testid="sandbox-banner"
          >
            <span>
              <strong>Aperçu :</strong> dans cet environnement, le bouton Télécharger peut être bloqué.{" "}
              <strong>Utilisez « Copier le lien »</strong> et collez-le dans un nouvel onglet de votre navigateur — le PDF se téléchargera automatiquement.
            </span>
          </div>
        )}

        <div className="flex-1 bg-slate-200 overflow-auto p-4" data-testid="pdf-pages-container">
          {loading && (
            <div className="h-full flex items-center justify-center text-slate-600">
              <Loader2 className="h-6 w-6 mr-2 animate-spin" />
              Génération du PDF…
            </div>
          )}
          {error && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-slate-700 p-6 text-center">
              <p className="font-display font-semibold text-lg">Erreur de chargement</p>
              <p className="text-sm text-slate-500 mt-1">{error}</p>
            </div>
          )}
          {!loading && !error && pages.length > 0 && (
            <div className="flex flex-col items-center gap-4">
              {pages.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Page ${i + 1}`}
                  className="max-w-full w-auto shadow-lg bg-white rounded-md"
                  data-testid={`pdf-page-${i + 1}`}
                />
              ))}
              <p className="text-xs text-slate-500 mt-2">
                Aperçu — cliquez sur <strong>Télécharger le PDF</strong> ci-dessus pour obtenir le fichier.
              </p>
            </div>
          )}
          {!loading && !error && pages.length === 0 && pdfDataUrl && (
            <div className="h-full flex flex-col items-center justify-center text-slate-700 p-6 text-center">
              <p className="font-display font-semibold text-lg">PDF prêt</p>
              <p className="text-sm text-slate-500 mt-1">Cliquez sur Télécharger ci-dessus.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper to build the preview URL on the frontend side.
export function buildPdfPreviewUrl({ projectName, clientName, address, postalCode, city, pacId, deperditionsOverrideKw, inputs, projectId }) {
  if (projectId) return `${API}/projects/${projectId}/pdf/preview`;
  const params = _buildParams({ projectName, clientName, address, postalCode, city, pacId, deperditionsOverrideKw, inputs });
  return `${API}/pdf/preview?${params.toString()}`;
}

export function buildPdfDownloadUrl({ projectName, clientName, address, postalCode, city, pacId, deperditionsOverrideKw, inputs, projectId }) {
  if (projectId) return `${API}/projects/${projectId}/pdf?download=1`;
  const params = _buildParams({ projectName, clientName, address, postalCode, city, pacId, deperditionsOverrideKw, inputs });
  params.set("download", "1");
  return `${API}/pdf?${params.toString()}`;
}

function _buildParams({ projectName, clientName, address, postalCode, city, pacId, deperditionsOverrideKw, inputs }) {
  const params = new URLSearchParams({
    surface: String(inputs.surface),
    hauteur: String(inputs.hauteur),
    isolation: inputs.isolation,
    t_interieur: String(inputs.t_interieur),
    zone: inputs.zone,
    altitude: String(inputs.altitude),
    emetteur: inputs.emetteur,
  });
  if (inputs.habitants != null) params.set("habitants", String(inputs.habitants));
  if (inputs.departement) params.set("departement", inputs.departement);
  if (projectName) params.set("name", projectName);
  if (clientName) params.set("client_name", clientName);
  if (address) params.set("address", address);
  if (postalCode) params.set("postal_code", postalCode);
  if (city) params.set("city", city);
  if (pacId) params.set("pac_id", pacId);
  if (deperditionsOverrideKw != null) params.set("deperditions_override_kw", String(deperditionsOverrideKw));
  return params;
}
